import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { WarehouseService } from '../../api/services/warehouseService';
import { CollectionPointService } from '../../api/services/collectionPointService';
import { WarehouseResponse } from '../../types/api/response/warehouseresponse';
import { CollectionPointResponse } from '../../types/api/response/collectionpointresponse';
import {
  CreateCollectionPointRequest,
  UpdateCollectionPointRequest,
} from '../../types/api/request/collectionpointrequest';

type SlideOverMode = 'add' | 'edit';

const emptyForm = {
  code: '',
  display_name: '',
  address: '',
  latitude: '',
  longitude: '',
  radius_meters: '200',
  is_active: true,
};

export const CollectionPointManagementPage = () => {
  const dispatch = useDispatch();
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [collectionPoints, setCollectionPoints] = useState<CollectionPointResponse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingCollectionPoints, setLoadingCollectionPoints] = useState(false);
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [slideOverMode, setSlideOverMode] = useState<SlideOverMode>('add');
  const [editingCp, setEditingCp] = useState<CollectionPointResponse | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<CollectionPointResponse | null>(null);

  const fetchWarehouses = useCallback(async () => {
    setLoadingWarehouses(true);
    try {
      const data = await WarehouseService.listWarehouses(false);
      setWarehouses(data);
      if (data.length > 0 && selectedWarehouseId === null) {
        setSelectedWarehouseId(data[0].id);
      }
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load warehouses.',
          severity: 'error',
        }),
      );
    } finally {
      setLoadingWarehouses(false);
    }
  }, [dispatch, selectedWarehouseId]);

  const fetchCollectionPoints = useCallback(
    async (warehouseId: number) => {
      setLoadingCollectionPoints(true);
      try {
        const data = await CollectionPointService.listCollectionPoints(warehouseId, false);
        setCollectionPoints(data);
      } catch (e: any) {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to load collection points.',
            severity: 'error',
          }),
        );
      } finally {
        setLoadingCollectionPoints(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    if (selectedWarehouseId !== null) {
      fetchCollectionPoints(selectedWarehouseId);
    }
  }, [selectedWarehouseId, fetchCollectionPoints]);

  const openAdd = () => {
    setSlideOverMode('add');
    setEditingCp(null);
    setForm(emptyForm);
    setSlideOverOpen(true);
  };

  const openEdit = (cp: CollectionPointResponse) => {
    setSlideOverMode('edit');
    setEditingCp(cp);
    setForm({
      code: cp.code,
      display_name: cp.display_name,
      address: cp.address ?? '',
      latitude: String(cp.latitude),
      longitude: String(cp.longitude),
      radius_meters: String(cp.radius_meters),
      is_active: cp.is_active,
    });
    setSlideOverOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedWarehouseId) return;

    const missingFields: string[] = [];
    if (slideOverMode === 'add' && !form.code.trim()) missingFields.push('Code');
    if (!form.display_name.trim()) missingFields.push('Display Name');
    if (!form.latitude.trim()) missingFields.push('Latitude');
    if (!form.longitude.trim()) missingFields.push('Longitude');
    if (!form.radius_meters.trim()) missingFields.push('Radius (metres)');
    if (missingFields.length > 0) {
      dispatch(
        showNotification({
          message: `Required fields are missing: ${missingFields.join(', ')}.`,
          severity: 'error',
        }),
      );
      return;
    }

    setSaving(true);
    try {
      if (slideOverMode === 'add') {
        const body: CreateCollectionPointRequest = {
          warehouse_id: selectedWarehouseId,
          code: form.code,
          display_name: form.display_name,
          address: form.address || undefined,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          radius_meters: parseInt(form.radius_meters),
          is_active: form.is_active,
        };
        await CollectionPointService.createCollectionPoint(body);
        dispatch(
          showNotification({
            message: 'Collection point created successfully.',
            severity: 'success',
          }),
        );
      } else {
        const body: UpdateCollectionPointRequest = {
          display_name: form.display_name,
          address: form.address || undefined,
          latitude: parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
          radius_meters: parseInt(form.radius_meters),
          is_active: form.is_active,
        };
        await CollectionPointService.updateCollectionPoint(editingCp!.id, body);
        dispatch(showNotification({ message: 'Collection point updated.', severity: 'success' }));
      }
      setSlideOverOpen(false);
      fetchCollectionPoints(selectedWarehouseId);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to save collection point.',
          severity: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusClick = (cp: CollectionPointResponse) => {
    if (cp.is_active) {
      setConfirmDeactivate(cp);
    } else {
      toggleStatus(cp, true);
    }
  };

  const toggleStatus = async (cp: CollectionPointResponse, newStatus: boolean) => {
    try {
      await CollectionPointService.updateCollectionPoint(cp.id, { is_active: newStatus });
      if (selectedWarehouseId) fetchCollectionPoints(selectedWarehouseId);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to update status.',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box>
      {/* Warehouse selector */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        {loadingWarehouses ? (
          <CircularProgress size={24} />
        ) : warehouses.length === 0 ? (
          <Typography color="text.secondary">
            No warehouses found. Create a warehouse to get started.
          </Typography>
        ) : (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Warehouse</InputLabel>
            <Select
              label="Warehouse"
              value={selectedWarehouseId ?? ''}
              onChange={(e) => {
                setSelectedWarehouseId(Number(e.target.value));
                setCollectionPoints([]);
              }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {warehouses.length > 0 && selectedWarehouseId !== null && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Collection Point
          </Button>
        )}
      </Box>

      {loadingCollectionPoints && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loadingCollectionPoints &&
        selectedWarehouseId !== null &&
        collectionPoints.length === 0 && (
          <Box py={8} textAlign="center" color="text.secondary">
            <Typography variant="h6">No collection points found</Typography>
            <Typography variant="body2">
              Add a collection point to this warehouse to get started.
            </Typography>
          </Box>
        )}

      {!loadingCollectionPoints && collectionPoints.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Radius</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {collectionPoints.map((cp) => (
                <TableRow key={cp.id}>
                  <TableCell>{cp.display_name}</TableCell>
                  <TableCell>
                    <code>{cp.code}</code>
                  </TableCell>
                  <TableCell>{cp.address ?? '—'}</TableCell>
                  <TableCell>{cp.radius_meters} m</TableCell>
                  <TableCell>
                    <Chip
                      label={cp.is_active ? 'Active' : 'Inactive'}
                      color={cp.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleStatusClick(cp)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openEdit(cp)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit modal */}
      <Dialog open={slideOverOpen} onClose={() => setSlideOverOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {slideOverMode === 'add' ? 'Add Collection Point' : 'Edit Collection Point'}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={slideOverMode === 'edit'}
              required
              helperText={
                slideOverMode === 'add'
                  ? 'Short unique identifier (e.g. CP-SUVA). Cannot be changed after creation.'
                  : undefined
              }
              fullWidth
            />
            <TextField
              label="Display Name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Address (optional)"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              fullWidth
            />
            <TextField
              label="Latitude"
              type="number"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Longitude"
              type="number"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Radius (metres)"
              type="number"
              value={form.radius_meters}
              onChange={(e) => setForm({ ...form, radius_meters: e.target.value })}
              required
              helperText="Distance from a bay within which the customer 'I have arrived' button is enabled."
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSlideOverOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        <DialogTitle>Deactivate Collection Point</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating {confirmDeactivate?.display_name} will hide it from the Bay Management
            selector. Its bays will remain in the database. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmDeactivate) toggleStatus(confirmDeactivate, false);
              setConfirmDeactivate(null);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
